import type React from 'react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCasePartnerState } from '@/components/case-partner/hooks/useCasePartnerState';
import { useDocumentSelections } from '@/components/case-partner/hooks/useDocumentSelections';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { shouldPreserveCasePartnerComposing } from '@/components/contexts/shouldPreserveCasePartnerComposing';
import { type UseChat, useChat } from '@/hooks/useChat';
import type { Document } from '@/hooks/useDocuments';
import type { DocumentSelection } from '@/types';

type CasePartnerUiState = ReturnType<typeof useCasePartnerState>;
type CasePartnerDocumentSelections = ReturnType<typeof useDocumentSelections>;

interface CasePartnerContextValue extends UseChat, CasePartnerUiState, CasePartnerDocumentSelections {
  matterGuid: string | null;
  isInitialized: boolean;
  isFullPageOpen: boolean;
  hasConversationActivity: boolean;
  documents: Document[] | undefined;
  routerQueryDocuments: string | string[] | undefined;
  routerQueryDocSel: string | undefined;
  activate: (matterGuid: string) => void;
  setFullPageOpen: (isOpen: boolean) => void;
  setDocuments: (documents: Document[] | undefined) => void;
  setDocumentsValidating: (isValidating: boolean) => void;
  setRouterQueryDocuments: (query: string | string[] | undefined) => void;
  setRouterQueryDocSel: (docSel: string | undefined) => void;
}

interface CasePartnerShellContextValue {
  matterGuid: string | null;
  isInitialized: boolean;
  isFullPageOpen: boolean;
  hasConversationActivity: boolean;
  isStreaming: boolean;
  isChatHistoryDocked: boolean;
}

interface CasePartnerOpenActionsContextValue {
  openDraftWithDocuments: (
    matterGuid: string,
    documentGuids: string[],
    optimisticSelections: DocumentSelection[],
    draft?: string
  ) => void;
  openExistingChat: (chatGuid: string) => Promise<void>;
}

const CasePartnerContext = createContext<CasePartnerContextValue | undefined>(undefined);
const CasePartnerShellContext = createContext<CasePartnerShellContextValue | undefined>(undefined);
const CasePartnerOpenActionsContext = createContext<CasePartnerOpenActionsContextValue | undefined>(undefined);

export const CasePartnerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [matterGuid, setMatterGuid] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFullPageOpen, setIsFullPageOpen] = useState(false);
  const [documents, setDocuments] = useState<Document[] | undefined>(undefined);
  const [routerQueryDocuments, setRouterQueryDocuments] = useState<string | string[] | undefined>(undefined);
  const [routerQueryDocSel, setRouterQueryDocSel] = useState<string | undefined>(undefined);
  const [isDocumentsValidating, setIsDocumentsValidating] = useState(false);
  const lastSelectedCaseRef = useRef<string | null>(null);
  const { selectedCase } = useCaseContext();

  const chat = useChat();
  const uiState = useCasePartnerState();
  const documentSelections = useDocumentSelections({
    documents,
    routerQueryDocuments,
    routerQueryDocSel,
    isDocumentsValidating,
  });

  const activate = useCallback((nextMatterGuid: string) => {
    setMatterGuid(nextMatterGuid);
    setIsInitialized(true);
  }, []);

  const isStreamingRef = useRef(chat.isStreaming);
  isStreamingRef.current = chat.isStreaming;

  useEffect(() => {
    if (!selectedCase) return;

    if (lastSelectedCaseRef.current && lastSelectedCaseRef.current !== selectedCase) {
      const search = typeof window === 'undefined' ? '' : window.location.search;
      const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
      if (!shouldPreserveCasePartnerComposing(search, selectedCase, pathname)) {
        documentSelections.clearSelections();
        uiState.setUserInput('');
        uiState.setComposerQuote(null);
        uiState.setShowAllDocuments(false);
        uiState.setShowDocumentSelector(false);
        uiState.setIsComposingNewChat(false);
        uiState.setPromptSuggestions([]);
        uiState.setShowPromptSuggestions(false);
        uiState.setHasShownInitialSuggestions(false);
        uiState.resetWebSearchToDefault();
        setDocuments(undefined);
        setRouterQueryDocuments(undefined);
        setRouterQueryDocSel(undefined);
      }
    }

    lastSelectedCaseRef.current = selectedCase;
  }, [
    selectedCase,
    documentSelections.clearSelections,
    setRouterQueryDocSel,
    uiState.setComposerQuote,
    uiState.setHasShownInitialSuggestions,
    uiState.setIsComposingNewChat,
    uiState.setPromptSuggestions,
    uiState.setShowAllDocuments,
    uiState.setShowDocumentSelector,
    uiState.setShowPromptSuggestions,
    uiState.setUserInput,
    uiState.resetWebSearchToDefault,
  ]);

  const hasConversationActivity = (
    chat.messages.length > 0 ||
    chat.isStreamingCurrentChat ||
    Boolean(chat.currentChatGuid)
  );

  const shellValue = useMemo(() => ({
    matterGuid,
    isInitialized,
    isFullPageOpen,
    hasConversationActivity,
    isStreaming: chat.isStreaming || chat.hasActiveGeneration,
    isChatHistoryDocked: isFullPageOpen && uiState.isSidebarVisible,
  }), [
    matterGuid,
    isInitialized,
    isFullPageOpen,
    hasConversationActivity,
    chat.isStreaming,
    chat.hasActiveGeneration,
    uiState.isSidebarVisible,
  ]);

  const openDraftWithDocuments = useCallback((
    nextMatterGuid: string,
    documentGuids: string[],
    optimisticSelections: DocumentSelection[],
    draft?: string
  ) => {
    activate(nextMatterGuid);
    uiState.setIsSidebarVisible(false);
    uiState.setShowDocumentSelector(false);
    uiState.setShowAllDocuments(false);
    uiState.setShowPromptSuggestions(false);
    uiState.setHasShownInitialSuggestions(false);
    documentSelections.clearSelections();

    if (isStreamingRef.current) {
      uiState.setIsComposingNewChat(true);
    } else {
      chat.startNewChat();
      uiState.setIsComposingNewChat(true);
    }
    uiState.resetWebSearchToDefault();

    uiState.setComposerQuote(null);
    uiState.setUserInput(draft ?? '');

    if (optimisticSelections.length > 0) {
      documentSelections.setDocumentSelections(new Map(
        optimisticSelections.map((selection) => [selection.guid, selection])
      ));
    }

    setRouterQueryDocuments(documentGuids);
    setRouterQueryDocSel(undefined);
  }, [
    activate,
    chat.startNewChat,
    documentSelections.clearSelections,
    documentSelections.setDocumentSelections,
    uiState.setHasShownInitialSuggestions,
    uiState.setIsComposingNewChat,
    uiState.setIsSidebarVisible,
    uiState.setShowAllDocuments,
    uiState.setShowDocumentSelector,
    uiState.setShowPromptSuggestions,
    uiState.setComposerQuote,
    uiState.setUserInput,
    uiState.resetWebSearchToDefault,
  ]);

  const openExistingChat = useCallback(async (chatGuid: string) => {
    if (selectedCase) {
      activate(selectedCase);
    }

    uiState.setIsSidebarVisible(false);
    uiState.setShowDocumentSelector(false);
    uiState.setShowAllDocuments(false);
    uiState.setShowPromptSuggestions(false);
    uiState.setHasShownInitialSuggestions(true);
    uiState.setIsComposingNewChat(false);
    uiState.setComposerQuote(null);
    documentSelections.clearSelections();

    const result = await chat.loadChat(chatGuid);
    if (result) {
      uiState.setWebSearchEnabled(Boolean(result.lastUserWebSearch));
    }
  }, [
    activate,
    chat.loadChat,
    documentSelections.clearSelections,
    selectedCase,
    uiState.setWebSearchEnabled,
    uiState.setComposerQuote,
    uiState.setHasShownInitialSuggestions,
    uiState.setIsComposingNewChat,
    uiState.setIsSidebarVisible,
    uiState.setShowAllDocuments,
    uiState.setShowDocumentSelector,
    uiState.setShowPromptSuggestions,
  ]);

  const openActionsValue = useMemo(() => ({
    openDraftWithDocuments,
    openExistingChat,
  }), [openDraftWithDocuments, openExistingChat]);

  return (
    <CasePartnerShellContext.Provider value={shellValue}>
      <CasePartnerOpenActionsContext.Provider value={openActionsValue}>
        <CasePartnerContext.Provider
          value={{
            ...chat,
            ...uiState,
            ...documentSelections,
            matterGuid,
            isInitialized,
            isFullPageOpen,
            hasConversationActivity,
            documents,
            routerQueryDocuments,
            routerQueryDocSel,
            activate,
            setFullPageOpen: setIsFullPageOpen,
            setDocuments,
            setDocumentsValidating: setIsDocumentsValidating,
            setRouterQueryDocuments,
            setRouterQueryDocSel,
          }}
        >
          {children}
        </CasePartnerContext.Provider>
      </CasePartnerOpenActionsContext.Provider>
    </CasePartnerShellContext.Provider>
  );
};

export const useCasePartner = () => {
  const context = useContext(CasePartnerContext);
  if (!context) {
    throw new Error('useCasePartner must be used within CasePartnerProvider');
  }

  return context;
};

export const useCasePartnerShell = () => {
  const context = useContext(CasePartnerShellContext);
  if (!context) {
    throw new Error('useCasePartnerShell must be used within CasePartnerProvider');
  }

  return context;
};

export const useCasePartnerOpenActions = () => {
  const context = useContext(CasePartnerOpenActionsContext);
  if (!context) {
    throw new Error('useCasePartnerOpenActions must be used within CasePartnerProvider');
  }

  return context;
};
